/**
 * Import Wizard — 5-step flow
 *   1. Source: local / Google Drive / OneDrive / Dropbox
 *   2. Browse + pick files
 *   3. Detecting (progress)
 *   4. Two-column review (Include / Skip)
 *   5. Upload progress + summary
 */

import { useState } from 'react';
import { Upload } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import type { CloudFile, FileRef, ImportSource } from '@/services/api/import';
import Step1Source from './steps/Step1Source';
import Step2Browse from './steps/Step2Browse';
import Step3Detecting from './steps/Step3Detecting';
import Step4Review from './steps/Step4Review';
import Step5Upload from './steps/Step5Upload';

type Step = 1 | 2 | 3 | 4 | 5;

export default function ImportWizardPage() {
  const [step, setStep] = useState<Step>(1);
  const [source, setSource] = useState<ImportSource | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [picked, setPicked] = useState<FileRef[]>([]);
  const [localFiles, setLocalFiles] = useState<File[]>([]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Upload}
        title="Import Documents"
        description="Bring files in from local, Google Drive, OneDrive or Dropbox. Auto-categorised, deduped, and routed to your clients."
      />

      <Stepper step={step} />

      {step === 1 && (
        <Step1Source
          onPick={(s) => { setSource(s); setStep(2); }}
        />
      )}
      {step === 2 && source && (
        <Step2Browse
          source={source}
          onBack={() => setStep(1)}
          onPicked={(refs, files) => {
            setPicked(refs);
            if (files) setLocalFiles(files);
            setStep(3);
          }}
        />
      )}
      {step === 3 && source && (
        <Step3Detecting
          source={source}
          fileRefs={picked}
          localFiles={localFiles}
          onReady={(id) => { setSessionId(id); setStep(4); }}
          onError={() => setStep(2)}
        />
      )}
      {step === 4 && sessionId && (
        <Step4Review
          sessionId={sessionId}
          onCommit={() => setStep(5)}
          onCancel={() => { setStep(1); setSessionId(null); }}
        />
      )}
      {step === 5 && sessionId && (
        <Step5Upload
          sessionId={sessionId}
          onDone={() => { setStep(1); setSessionId(null); setPicked([]); setLocalFiles([]); setSource(null); }}
        />
      )}
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const labels = ['Source', 'Browse', 'Detect', 'Review', 'Upload'];
  return (
    <div className="flex items-center gap-2 text-xs">
      {labels.map((label, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div
              className={
                'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ' +
                (done ? 'bg-emerald-500 text-white' :
                 active ? 'bg-primary text-white' :
                 'bg-muted text-muted-foreground')
              }
            >
              {idx}
            </div>
            <span className={active ? 'font-semibold' : 'text-muted-foreground'}>{label}</span>
            {idx < 5 && <span className="text-muted-foreground/40">→</span>}
          </div>
        );
      })}
    </div>
  );
}
